import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {AutoLogin} from '../modules/auth/components/AutoLogin'
import {DEFAULT_AUTHENTICATED_ROUTE} from './constants'
import {Permissions, RequirePermission} from '../modules/auth/permissions'

const ModelListPage = lazy(() => import('../pages/modellist/Page'))
const MethodListPage = lazy(() => import('../pages/methodlist/Page'))
const RoleMethodListPage = lazy(() => import('../pages/rolemethodlist/Page'))
const RoleModelListPage = lazy(() => import('../pages/rolemodellist/Page'))
const MenuListPage = lazy(() => import('../pages/menulist/Page'))
const MenuRolePage = lazy(() => import('../pages/menurole/Page'))
const UserActionPage = lazy(() => import('../pages/useraction/Page'))
const CulturePage = lazy(() => import('../pages/culture/Page'))
const OrganizationPage = lazy(() => import('../pages/organization/Page'))
const DynamicReportPage = lazy(() => import('../pages/dynamicreport/DynamicReport'))
const CBeePage = lazy(() => import('../pages/cbbee/Page'))
const PerformancePage = lazy(() => import('../pages/performance/Page'))
const MoleSuiteCatalogPage = lazy(() => import('../pages/molesuite/MoleSuiteCatalogPage'))
const AbilityAdminPage = lazy(() => import('../pages/ability/AbilityAdminPage'))
const moleSuiteSharedPages = () => import('../pages/molesuite/MoleSuiteSharedPages')
const MoleSuiteWorkspacePage = lazy(() =>
  moleSuiteSharedPages().then((module) => ({default: module.MoleSuiteWorkspacePage}))
)
const MoleSuiteEmployeeInformationPage = lazy(() =>
  moleSuiteSharedPages().then((module) => ({default: module.MoleSuiteEmployeeInformationPage}))
)
const MoleSuiteBeeHivePage = lazy(() =>
  moleSuiteSharedPages().then((module) => ({default: module.MoleSuiteBeeHivePage}))
)
const MoleSuiteOrganizationTreePage = lazy(() =>
  moleSuiteSharedPages().then((module) => ({default: module.MoleSuiteOrganizationTreePage}))
)
const ApplicationUserPage = lazy(() => import('../pages/applicationuser/Page'))
const ApplicationRolePage = lazy(() => import('../pages/applicationrole/Page'))
const ApplicationUserRolePage = lazy(() => import('../pages/applicationuserrole/Page'))
const ApplicationUserClaimPage = lazy(() => import('../pages/applicationuserclaim/Page'))
const ApplicationRoleClaimPage = lazy(() => import('../pages/applicationroleclaim/Page'))
const TenantPage = lazy(() => import('../pages/tenant/Page'))
const SettingPage = lazy(() => import('../pages/setting/Page'))

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

const PrivateRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path='auth/autologin/:token' element={<AutoLogin />} />
        <Route path='dashboard' element={<Navigate to='/workspace' replace />} />
        <Route
          path='workspace/*'
          element={
            <RequirePermission permission={Permissions.ViewData}>
              <SuspensedView>
                <MoleSuiteWorkspacePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='employee-information/*'
          element={
            <RequirePermission permission={Permissions.ViewWorkforceProfileDetails}>
              <SuspensedView>
                <MoleSuiteEmployeeInformationPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='organization-tree/*'
          element={
            <RequirePermission permission={Permissions.ViewWorkforceProfileDetails}>
              <SuspensedView>
                <MoleSuiteOrganizationTreePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='beehive/*'
          element={
            <RequirePermission permission={Permissions.ImportData}>
              <SuspensedView>
                <MoleSuiteBeeHivePage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path='modellist/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><ModelListPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='methodlist/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><MethodListPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='rolemethodlist/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><RoleMethodListPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='rolemodellist/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><RoleModelListPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='menulist/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><MenuListPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='menurole/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><MenuRolePage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='useraction/*'
          element={
            <RequirePermission permission={Permissions.ViewAuditLogs}><SuspensedView><UserActionPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='culture/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><CulturePage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='organization/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><OrganizationPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='cbbee/*'
          element={
            <SuspensedView>
              <CBeePage />
            </SuspensedView>
          }
        />
        <Route
          path='performance/*'
          element={
            <RequirePermission permission={Permissions.ViewData}>
              <SuspensedView>
                <PerformancePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='select/ability/*'
          element={
            <RequirePermission permission={Permissions.ViewData}>
              <SuspensedView>
                <AbilityAdminPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='select/*'
          element={
            <RequirePermission permission={Permissions.ViewData}>
              <SuspensedView>
                <MoleSuiteCatalogPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='talent/*'
          element={
            <RequirePermission permission={Permissions.ViewData}>
              <SuspensedView>
                <MoleSuiteCatalogPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='dynamicreport/*'
          element={
            <RequirePermission permission={Permissions.ViewData}><SuspensedView><DynamicReportPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='applicationuser/*'
          element={
            <RequirePermission permission={Permissions.ManageUsers}><SuspensedView><ApplicationUserPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='applicationrole/*'
          element={
            <RequirePermission permission={Permissions.ManageUsers}><SuspensedView><ApplicationRolePage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='applicationuserrole/*'
          element={
            <RequirePermission permission={Permissions.ManageUsers}><SuspensedView><ApplicationUserRolePage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='applicationuserclaim/*'
          element={
            <RequirePermission permission={Permissions.ManageUsers}><SuspensedView><ApplicationUserClaimPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='applicationroleclaim/*'
          element={
            <RequirePermission permission={Permissions.ManageUsers}><SuspensedView><ApplicationRoleClaimPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='tenant/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><TenantPage /></SuspensedView></RequirePermission>
          }
        />
        <Route
          path='setting/*'
          element={
            <RequirePermission permission={Permissions.ManageSystem}><SuspensedView><SettingPage /></SuspensedView></RequirePermission>
          }
        />

        <Route index element={<Navigate to={DEFAULT_AUTHENTICATED_ROUTE} />} />
        <Route path='*' element={<Navigate to={DEFAULT_AUTHENTICATED_ROUTE} />} />
      </Route>
    </Routes>
  )
}

export {PrivateRoutes}
